import React from "react"
import { defineMessage, FormattedMessage, MessageDescriptor, useIntl } from "react-intl"
import { ArrowSubmitButton, ReimbursementDialogFraming, BackArrow } from "../shared/layouts"
import { Progress } from "#/components/views/modal/Progress"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { useReimbursementContext } from "#/components/context/reimbursement/GetReimbursedContext"
import { DialogActionsBar, Form, FormSelect } from "@carrotfertility/carotene-core-x"
import { Box, DialogContentText, DialogTitle } from "@carrotfertility/carotene-core"
import useBenefit from "#/components/dynamic-content/hooks/useGetBenefit"

type IntendedCarrierOption = {
  label: MessageDescriptor
  value: string
}

const baseIntendedCarrierOptions: IntendedCarrierOption[] = [
  { label: defineMessage({ defaultMessage: "Me" }), value: "MEMBER" },
  { label: defineMessage({ defaultMessage: "My legal spouse or tax-dependent partner" }), value: "LEGAL_PARTNER" },
  { label: defineMessage({ defaultMessage: "My non-married partner" }), value: "NON_MARRIED_PARTNER" }
]

const gestationalCarrierOption: IntendedCarrierOption = {
  label: defineMessage({ defaultMessage: "A gestational carrier" }),
  value: "GESTATIONAL_CARRIER"
}

interface FormValues {
  intendedCarrier: string
}

export const WhoIsIntendedCarrier = (): JSX.Element => {
  const { send, back } = useStateMachine()
  const { updateIntendedCarrier, state } = useReimbursementContext()
  const { data: benefit } = useBenefit()
  const intl = useIntl()

  const hasGestationalCarrierCoverage = benefit?.journeys?.gestationalCarrier?.hasEligibleExpenses

  const options = hasGestationalCarrierCoverage
    ? [...baseIntendedCarrierOptions, gestationalCarrierOption]
    : baseIntendedCarrierOptions

  const formattedOptions = options.map((option) => ({
    ...option,
    label: intl.formatMessage(option.label)
  }))

  const onSubmit = (values: FormValues): void => {
    const answerAsSelectOption = formattedOptions.find((o) => o.value === values.intendedCarrier)
    updateIntendedCarrier(answerAsSelectOption)
    send("")
  }

  return (
    <Form<FormValues> onSubmit={onSubmit} defaultValues={{ intendedCarrier: state.intendedCarrier?.value || "" }}>
      <Progress
        numSteps={3}
        step={2}
        label={intl.formatMessage({ defaultMessage: "About This Expense" })}
        inactiveStepBackgroundColor="#DBE6EE"
      />
      <BackArrow onClick={back} />
      <ReimbursementDialogFraming>
        <DialogTitle id="step-heading">
          <FormattedMessage defaultMessage="Who is the intended carrier?" />
        </DialogTitle>
        <Box sx={{ display: "flex", flexDirection: "column", gap: (theme) => theme.spacing(theme.tokens.spacing.lg) }}>
          <DialogContentText>
            <FormattedMessage defaultMessage="The intended carrier receives the donor material and would carry a pregnancy resulting from treatment." />
          </DialogContentText>
          <DialogContentText>
            <FormattedMessage defaultMessage="This helps us determine if the care may be a qualified medical expense." />
          </DialogContentText>
          <FormSelect
            aria-labelledby="step-heading"
            name="intendedCarrier"
            controllerProps={{
              rules: {
                required: intl.formatMessage({ defaultMessage: "Required" })
              }
            }}
            options={formattedOptions}
            label={intl.formatMessage({ defaultMessage: "Select" })}
          />
        </Box>
      </ReimbursementDialogFraming>
      <DialogActionsBar
        primaryAction={
          <ArrowSubmitButton>
            <FormattedMessage defaultMessage="Continue" />
          </ArrowSubmitButton>
        }
      />
    </Form>
  )
}
